import React from 'react';
import { Box, Container, makeStyles, useMediaQuery } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import SectionHeading from 'components/SectionHeading';
import ProductHeader from '../Header';
import ProductDetailedButton from '../DetailedButton/DetailedButton';
import AppNotFound from '../../../components/NotFound';
import { sendEvent } from '../../../utils/google-analytics';
import { Product } from '../../../types';
import Gallery from '../DetailsStep/Gallery';
import Steps from '../Steps/Steps';
import FAQSection from '../../../Homepage/components/FAQSection/FAQSection';

// styles
const useStyles = makeStyles((theme) => ({
  root: {
    // Bottom gap to footer is handled by App main (pb={10})
  },
  layout: {
    display: 'flex',
    gap: theme.spacing(4),
    padding: theme.spacing(0, 0, 4),
    flexWrap: 'nowrap',
  },
  list: {
    listStyle: 'none',
    padding: 0,
    '& li button svg': {
      fill: `${theme.palette.secondary.main} !important`,
    },
    '& li button svg *': {
      fill: `${theme.palette.secondary.main} !important`,
    },
  },
  listItem: {
    color: theme.palette.secondary.main,
  },
}));

// props; exporting to tests
export interface ProductRepairStepViewProps {
  repairs: Product[];
  onRepairSelect(repair: Product): void;
  categoryId?: string;
  typeId?: string;
  backLinkHref?: string;
}

/**
 * Product repair step presentational component.
 */
export default function ProductRepairStepView({ repairs, onRepairSelect, categoryId, typeId, backLinkHref = '..' }: ProductRepairStepViewProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const headingHtmlId = 'ProductRepairStep-heading';
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));
  const categoryImages = repairs[0]?.productType?.images || [];

  if (repairs.length === 0) {
    return <AppNotFound />;
  }

  const onProductDetailClick = (x: Product) => {
    onRepairSelect(x);
    //@ts-ignore
    sendEvent('repair', `repair_garment_selected-garment_${x.name}`, x.name);
  };

  function renderButtons() {
    const buttons = repairs.map((x, i) => (
      <Box component="li" key={i} mb={2} className={classes.listItem}>
        <ProductDetailedButton onClick={() => onProductDetailClick(x)} description={x.shortDescription} {...x} />
      </Box>
    ));
    return buttons;
  }

  return (
    <Box className={classes.root}>
      <Container maxWidth="xl">
        <Steps currentStep={2} />
        <Box mb={4} />

        <div
          className={classes.layout}
          style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
        >
          <Box width="100%">
            <ProductHeader categoryId={categoryId} typeId={typeId} backLinkHref={backLinkHref} />
            <Box mb={3} />
            <SectionHeading id={headingHtmlId} component="h1" size="large">
              {t('Select the type of repair')}
            </SectionHeading>
            <Box mb={3} />
            <Box
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                flexDirection: isMobile ? 'column-reverse' : 'row',
              }}
            >
              <Box width={isMobile ? '100%' : '50%'}>
                <ul aria-labelledby={headingHtmlId} className={classes.list}>
                  {renderButtons()}
                </ul>
              </Box>
              {!!categoryImages?.length && (
                <Box
                  width={isMobile ? '100%' : '46%'}
                  style={{ paddingBottom: isMobile ? '30px' : undefined }}
                >
                  <Gallery images={categoryImages} hideThumbs />
                </Box>
              )}
            </Box>
          </Box>
        </div>
      </Container>
      <FAQSection variant="product" />
    </Box>
  );
}
